<template>
  <main class="flex min-h-screen flex-col bg-brand-surface-muted text-brand-ink">
    <header class="sticky top-0 z-30 border-b border-brand-line bg-brand-surface/90 backdrop-blur supports-backdrop-filter:bg-brand-surface/75">
      <div class="relative mx-auto flex min-h-20 w-full max-w-7xl items-center justify-between gap-4 px-4 md:px-8">
        <button
          class="group flex min-w-0 items-center gap-3 text-left transition-opacity hover:opacity-80"
          type="button"
          @click="router.push({ name: 'onboarding' })"
        >
          <BrandLogo
            :name="branding.companyLogo"
            :label="branding.portalName"
            class="h-20 w-auto text-brand-ink"
          />
        </button>

        <button
          v-if="branding.productLogo"
          class="absolute left-1/2 hidden -translate-x-1/2 items-center gap-2 text-brand-ink transition-opacity hover:opacity-80 md:flex"
          type="button"
          :aria-label="branding.portalName"
          @click="router.push({ name: 'onboarding' })"
        >
          <BrandLogo
            :name="branding.productLogo"
            :label="branding.portalName"
            class="h-14 w-auto"
          />
          <span class="text-[11px] font-bold uppercase tracking-[0.2em]">{{ t("app.portalLabel") }}</span>
        </button>

        <div class="flex min-w-0 items-center gap-4">
          <div class="hidden min-w-0 text-right lg:block">
            <div class="truncate text-sm font-semibold leading-tight">{{ user?.name }}</div>
            <div class="truncate text-xs text-brand-muted">{{ user?.email }}</div>
          </div>
          <label class="sr-only" for="portal-locale">{{ t("app.locale.switcherLabel") }}</label>
          <select
            id="portal-locale"
            class="h-10 rounded-none border border-brand-line bg-brand-surface px-3 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-ink"
            :aria-label="t('app.locale.switcherLabel')"
            :value="locale"
            @change="setLocale(($event.target as HTMLSelectElement).value as Locale)"
          >
            <option v-for="option in availableLocales" :key="option" :value="option">
              {{ t(`app.locale.${option}`) }}
            </option>
          </select>
          <Button
            class="h-10 rounded-none border-0 bg-brand-fill px-6 text-[11px] font-bold uppercase tracking-[0.12em] text-white shadow-none transition-colors hover:bg-brand-fill-hover"
            size="sm"
            @click="onLogout"
          >
            <LogOut class="size-4" />
            {{ t("auth.logout") }}
          </Button>
        </div>
      </div>
    </header>

    <div class="mx-auto w-full max-w-5xl flex-1 px-4 py-12 md:px-8 md:py-16">
      <slot />
    </div>

    <AppFooter />

    <DebugCard :title="debugTitle" :data="resolvedDebugData" />
  </main>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { LogOut } from "lucide-vue-next";
import { toast } from "vue-sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useCustomerSession } from "@/features/customer-session";
import { getDebugEnv } from "@/lib/debug-env";
import { useI18n, type Locale } from "@/lib/i18n";
import AppFooter from "@/components/AppFooter.vue";
import BrandLogo from "@/components/brand/BrandLogo.vue";
import DebugCard from "@/components/debug/DebugCard.vue";
import { useConfig } from "@/lib/config";
import { getBranding } from "@/lib/branding";

const props = withDefaults(
  defineProps<{
    debugTitle?: string;
    debugData?: unknown;
  }>(),
  {
    debugTitle: "",
    debugData: undefined,
  },
);

const router = useRouter();
const route = useRoute();
const auth = useAuth();
const customerSession = useCustomerSession();
const user = computed(() => auth.state.user);
const { availableLocales, locale, setLocale, t } = useI18n();
const branding = getBranding(useConfig().branding);
const resolvedDebugData = computed(
  () =>
    props.debugData === undefined
      ? {
          route: {
            name: route.name,
            path: route.path,
            params: route.params,
            query: route.query,
          },
          user: user.value,
          customer: customerSession.current,
          env: getDebugEnv(),
        }
      : props.debugData,
);

async function onLogout() {
  await auth.logout();
  customerSession.clear();
  toast.info(t("auth.loggedOut"));
  router.replace({ name: "login" });
}
</script>
